import React, { useState, useEffect } from "react";
import Table from "../../core/pagination/datatable";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getProductDashboardData } from "../../core/api/products";
import { getProductOrderPrediction } from "../../core/api/mediaplan";
import { get100DaysROR } from "../../core/api/report";

const ProductDeliverySummary = () => {
  const selectedClients = useSelector((state) => state.selectedClients);
  const [productData, setProductData] = useState([]);

  const dateRange = [dayjs(), dayjs().add(365, "day")];
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (!selectedClients?.length) return;

        const startDate = dateRange[0].format("YYYY-MM-DD");
        const endDate = dateRange[1].format("YYYY-MM-DD");

        const [dashboardRes, predictionRes, rorRes] = await Promise.all([
          getProductDashboardData(selectedClients, startDate, endDate),
          getProductOrderPrediction(selectedClients),
          get100DaysROR(selectedClients),
        ]);

        const dashboard = dashboardRes.data;
        const prediction = predictionRes.data;
        const ror = rorRes.data;

        const groupedByVariation = {};
        prediction.forEach((item) => {
          const pv = item.product_variation_code;
          if (!groupedByVariation[pv]) groupedByVariation[pv] = [];
          groupedByVariation[pv].push(item);
        });

        const enhanced = dashboard.map((product) => {
          const formattedDeliveries = (
            product.expected_delivery_within_range || []
          ).map((delivery) => ({
            ...delivery,
            formatted_date: delivery.expected_delivery_date
              ? dayjs(delivery.expected_delivery_date).format("YYYY-MM-DD")
              : "-",
          }));

          const earliestDeliveryDate =
            product.expected_delivery_within_range?.length > 0
              ? dayjs(
                  product.expected_delivery_within_range[0]
                    .expected_delivery_date
                )
              : null;

          const calculateOutOfStockDate = () => {
            const stockToday = product.stockQty || 0;
            const allDeliveries = [
              ...(product.expected_delivery_within_range || []),
              ...(product.expected_delivery_after_end_date || []),
            ];
            const productCampaigns =
              groupedByVariation[product.productVariation] || [];

            let stock = stockToday;
            let current = dayjs();
            let outDate = null;
            let days = 0;
            const maxDays = 90;

            while (days < maxDays) {
              let dailyDemand = 0;
              productCampaigns.forEach((campaign) => {
                const mailDate = dayjs(campaign.mail_date);
                const dayDiff = current.diff(mailDate, "day");
                if (dayDiff >= 0 && dayDiff <= 100) {
                  const rorValue = ror[dayDiff] || 0;
                  const qty =
                    (((campaign.target_mail_qty || 0) *
                      (campaign.estimated_ror || 0)) /
                      100) *
                    (rorValue / 100);
                  dailyDemand += qty * campaign.qty_per_order;
                }
              });

              const dailyDeliveries = allDeliveries
                .filter((d) => {
                  const date = dayjs(d.expected_delivery_date);
                  return date.isSame(current, "day");
                })
                .reduce((sum, d) => sum + (d.delivery_qty || 0), 0);

              stock -= dailyDemand;
              stock += dailyDeliveries;

              if (stock < 0 && outDate === null) {
                outDate = current.format("YYYY-MM-DD");
                break;
              }

              current = current.add(1, "day");
              days++;
            }

            return outDate || ">90d";
          };

          const outOfStockDate = calculateOutOfStockDate();
          const outOfStockDateObj =
            outOfStockDate !== ">90d" ? dayjs(outOfStockDate) : null;

          return {
            ...product,
            formattedDeliveries,
            earliestDeliveryDate,
            outOfStockDate,
            outOfStockDateObj,
          };
        });

        const filteredProducts = enhanced.filter(
          (product) => product.expected_delivery_within_range?.length > 0
        );

        const sortedProducts = filteredProducts.sort((a, b) => {
          if (!a.outOfStockDateObj && !b.outOfStockDateObj) {
            if (!a.earliestDeliveryDate) return 1;
            if (!b.earliestDeliveryDate) return -1;
            return a.earliestDeliveryDate.diff(b.earliestDeliveryDate);
          }

          if (!a.outOfStockDateObj) return 1;
          if (!b.outOfStockDateObj) return -1;

          return a.outOfStockDateObj.diff(b.outOfStockDateObj);
        });

        setProductData( sortedProducts );
        setLoading(false);
      } catch (error) {
        console.error(
          "Error fetching data for DeliverySummary component:",
          error
        );
      }
    };

    fetchData();
  }, [selectedClients]);

  const columnsDelivery = [
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      fixed: "left",
      width: 120,
    },
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      fixed: "left",
      width: 150,
    },
    {
      title: "Product Variation",
      dataIndex: "productVariation",
      key: "productVariation",
      fixed: "left",
      width: 150,
    },
    {
      title: "Out of Stock Date",
      dataIndex: "outOfStockDate",
      key: "outOfStockDate",
      fixed: "left",
      width: 150,
      render: (text) => {
        if (text === ">90d") {
          return (
            <div
              style={{
                backgroundColor: "#52c41a",
                color: "white",
                padding: "2px 4px",
                borderRadius: "4px",
                fontWeight: "normal",
                display: "inline-block",
                width: "100%",
                textAlign: "center",
              }}
            >
              {text}
            </div>
          );
        }

        const daysUntil = dayjs(text).diff(dayjs(), "day");

        let backgroundColor;
        let textColor = "white";
        let fontWeight = "bold";

        if (daysUntil <= 7) {
          backgroundColor = "#f5222d";
        } else if (daysUntil <= 30) {
          backgroundColor = "#ff4d4f";
        } else if (daysUntil <= 60) {
          backgroundColor = "#ff7875";
        } else if (daysUntil <= 90) {
          backgroundColor = "#fa8c16";
          fontWeight = "normal";
        } else {
          backgroundColor = "#fadb14";
          textColor = "rgba(0, 0, 0, 0.65)";
          fontWeight = "normal";
        }

        return (
          <div
            style={{
              backgroundColor,
              color: textColor,
              padding: "2px 4px",
              borderRadius: "4px",
              fontWeight,
              display: "inline-block",
              width: "100%",
              textAlign: "center",
            }}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Total Qty on Delivery",
      dataIndex: "qtyOnDeliveryWithinRange",
      key: "qtyOnDeliveryWithinRange",
      width: 150,
    },
    ...Array.from({
      length: Math.max(
        ...productData.map((p) => p.expected_delivery_within_range?.length || 0)
      ),
    }).map((_, i) => ({
      title: `Expected Delivery ${i + 1}`,
      children: [
        {
          title: "Expected Date",
          dataIndex: [
            "expected_delivery_within_range",
            i,
            "expected_delivery_date",
          ],
          key: `expected_within_date_${i}`,
          render: (_, record) => {
            const date =
              record.expected_delivery_within_range?.[i]
                ?.expected_delivery_date;

            if (!date) return "-";

            const formattedDate = dayjs(date).format("YYYY-MM-DD");
            const isToday = dayjs(date).isSame(dayjs(), "day");

            return (
              <div
                style={{
                  backgroundColor: isToday ? "#f5222d" : "transparent",
                  color: isToday ? "white" : "inherit",
                  padding: isToday ? "2px 4px" : "0",
                  borderRadius: isToday ? "4px" : "0",
                  fontWeight: isToday ? "bold" : "normal",
                  display: "inline-block",
                  width: isToday ? "100%" : "auto",
                  textAlign: isToday ? "center" : "left",
                }}
              >
                {formattedDate}
                {isToday && " (TODAY)"}
              </div>
            );
          },
        },
        {
          title: "Delivery Qty",
          dataIndex: ["expected_delivery_within_range", i, "delivery_qty"],
          key: `expected_within_qty_${i}`,
          render: (_, record) =>
            record.expected_delivery_within_range?.[i]?.delivery_qty || "-",
          width: 140,
        },
      ],
    })),
  ];

  return (
    <Table
      columns={columnsDelivery}
      dataSource={productData}
      scroll={{ x: "max-content" }}
      loading={loading}
    />
  );
};

export default ProductDeliverySummary;
